import { Link } from "react-router-dom";
import { ArrowLeft, Target, Heart, Truck } from "lucide-react";
import Footer from "@/components/Footer";

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
        <div className="max-w-[1800px] mx-auto flex justify-between items-center">
          <Link to="/" className="font-['Syne'] font-extrabold text-2xl tracking-tight">
            CUE.PO
          </Link>
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-[#002FA7] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
            Turning Goals Into Art
          </h1>
          <p className="text-xl md:text-2xl opacity-90">
            We believe that seeing your goals every day is the first step to achieving them.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <main className="flex-1 px-6 md:px-12 lg:px-24 py-24">
        <div className="max-w-4xl mx-auto">
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8">Our Story</h2>
            <div className="space-y-6 text-lg text-gray-600">
              <p>
                CUE.PO was born from a simple idea: what if you could turn your goals into 
                beautiful art that inspires you every single day?
              </p>
              <p>
                We noticed something about goal-setting. People write their goals in notebooks 
                that get buried in drawers. They create vision boards that end up in closets. 
                The best intentions fade without constant reminders.
              </p>
              <p>
                So we created CUE.PO — a way to transform your personal goals into minimalist, 
                elegant posters that deserve a place on your wall. Not hidden away, but front 
                and center where you'll see them every morning when you wake up and every night 
                before you sleep.
              </p>
              <p>
                Your goals. Your space. Your daily reminder that you're working toward something meaningful.
              </p>
            </div>
          </section>

          {/* Values Section */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-12">What We Stand For</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#002FA7] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Purpose-Driven Design</h3>
                <p className="text-gray-600">
                  Every poster is designed with intention. Clean, focused, and meaningful — 
                  just like your goals should be.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-[#002FA7] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Quality That Lasts</h3>
                <p className="text-gray-600">
                  Premium matte paper, archival inks, and careful printing. Your goals 
                  deserve to look good for years to come.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-[#002FA7] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Direct to You</h3>
                <p className="text-gray-600">
                  No middlemen, no warehouses. Each poster is printed on-demand and 
                  shipped directly to your door.
                </p>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8">How It Works</h2>
            <div className="space-y-8">
              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Define Your Goals</h3>
                  <p className="text-gray-600">
                    Enter up to three goals that matter most to you. They can be big dreams or 
                    daily habits — whatever you want to keep top of mind.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Choose Your Size</h3>
                  <p className="text-gray-600">
                    Select from small (perfect for desks), medium (great for living spaces), 
                    or large (a bold statement piece).
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">We Print & Ship</h3>
                  <p className="text-gray-600">
                    Your custom poster is printed on premium paper and shipped directly to you. 
                    Most orders arrive within 5-12 business days.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Stay Inspired</h3>
                  <p className="text-gray-600">
                    Hang your poster where you'll see it daily. Let your goals be the first 
                    thing you see in the morning and the last thing at night.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-gray-50 py-16 px-8 rounded-sm">
            <h2 className="text-3xl font-extrabold mb-4">Ready to Define Your Goals?</h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Join thousands of ambitious people who've turned their goals into daily inspiration.
            </p>
            <Link to="/">
              <button className="btn-primary">
                Create Your Poster
              </button>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;
